import React, { memo, useCallback, useRef } from 'react';
import { Markdown, Text } from '@Sage/hoi-poi-ui';

import Map from 'components/Map';
import MapMarker from 'components/Map/MapMarker';
import MapMarkerWithTooltip from 'components/Map/MapMarkerWithTooltip';
import { COMPANY_ICON, COMPANY_ICON_GREY } from 'constants/Images';
import { getLiteral } from 'utils/getLiteral';

import { DEFAULT_MAP_ZOOM } from './constants';
import { normalizeOriginCoordinates } from './helpers/nearbyRequest';
import LeadsMapOriginControl from './LeadsMapOriginControl';

const LEADS_MAP_OPTIONS = {
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    rotateControl: false,
    scaleControl: false,
};

const MAP_TOOLTIP_TEXT_COLOR = 'var(--primary-white)';

/** Tooltip: name, then business type (category) on the next line — no separate field label. */
const buildLeadTooltipContent = (lead) => (
    <div className="fm-leads-modal__map-tooltip">
        <Text as="div" type="caption" bold color={MAP_TOOLTIP_TEXT_COLOR}>
            {lead.name}
        </Text>
        {lead.category ? (
            <Text
                as="div"
                type="caption"
                color={MAP_TOOLTIP_TEXT_COLOR}
                className="fm-leads-modal__map-tooltip-type"
            >
                {lead.category}
            </Text>
        ) : null}
    </div>
);

const isLeadSelected = (selectedIds, leadId) =>
    selectedIds.some((sid) => String(sid) === String(leadId));

/** Markers match the list: same bucket as `LeadList` (`activeLeads`). */
const LeadsMap = ({
    activeLeads = [],
    selectedIds = [],
    origin,
    initialOrigin = null,
    zoom = DEFAULT_MAP_ZOOM,
    onMapViewChange,
    onOriginChange,
    onToggleLead,
    onMapReadyChange,
}) => {
    const mapRef = useRef(null);
    const isDraggingPinRef = useRef(false);

    const resetTarget = initialOrigin;
    const resetToMyLocationLabel = getLiteral('action_leads_reset_to_my_location');

    const handleRecenterToStart = useCallback(() => {
        if (!resetTarget) return;
        onOriginChange(resetTarget);
        mapRef.current?.setCenter?.(resetTarget);
    }, [onOriginChange, resetTarget]);

    const syncMapViewFromInstance = useCallback(() => {
        if (isDraggingPinRef.current) return;

        const center = mapRef.current?.getCenter?.();
        const nextZoom = mapRef.current?.getZoom?.();
        if (!center || nextZoom == null) return;

        const normalized = normalizeOriginCoordinates(center);
        if (!normalized) return;

        onMapViewChange?.({ lat: normalized.lat, lng: normalized.lng, zoom: nextZoom });
    }, [onMapViewChange]);

    const handlePinDragStart = useCallback(() => {
        isDraggingPinRef.current = true;
    }, []);

    const handlePinDragEnd = useCallback(
        (event) => {
            const lat = event?.latLng?.lat?.();
            const lng = event?.latLng?.lng?.();
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                isDraggingPinRef.current = false;
                return;
            }

            const next = { lat, lng };
            const nextZoom = mapRef.current?.getZoom?.() ?? zoom;

            mapRef.current?.setCenter?.(next);
            onMapViewChange?.({ ...next, zoom: nextZoom });

            requestAnimationFrame(() => {
                isDraggingPinRef.current = false;
            });
        },
        [onMapViewChange, zoom],
    );

    const handleMapLoad = useCallback(() => {
        onMapReadyChange?.(true);
        syncMapViewFromInstance();
    }, [onMapReadyChange, syncMapViewFromInstance]);

    const handleMapUnmount = useCallback(() => {
        onMapReadyChange?.(false);
    }, [onMapReadyChange]);

    const handleMapIdle = useCallback(() => {
        syncMapViewFromInstance();
    }, [syncMapViewFromInstance]);

    return (
        <div className="fm-leads-modal__map">
            <Map
                ref={mapRef}
                center={origin}
                zoom={zoom}
                options={LEADS_MAP_OPTIONS}
                onLoadMap={handleMapLoad}
                onIdle={handleMapIdle}
                onUnmount={handleMapUnmount}
            >
                <LeadsMapOriginControl
                    resetTarget={resetTarget}
                    ariaLabel={resetToMyLocationLabel}
                    onRecenter={handleRecenterToStart}
                />
                <MapMarker
                    position={origin}
                    draggable
                    onDragStart={handlePinDragStart}
                    onDragEnd={handlePinDragEnd}
                />
                {activeLeads.map((lead) => (
                    <MapMarkerWithTooltip
                        key={lead.id}
                        position={lead.position}
                        icon={{
                            url: isLeadSelected(selectedIds, lead.id)
                                ? COMPANY_ICON
                                : COMPANY_ICON_GREY,
                        }}
                        tooltipContent={buildLeadTooltipContent(lead)}
                        onClick={() => onToggleLead(lead.id)}
                    />
                ))}
            </Map>
            <div className="fm-leads-modal__map-help">
                <Markdown content={getLiteral('label_leads_drag_pin_help')} />
            </div>
        </div>
    );
};

export default memo(LeadsMap);
